import { useState, useCallback, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { downloadFile, extractFileNameFromHeaders } from '../../../../util/DownloadUtil';

import { retrieveSiteQueryStatusReport, downloadSiteQueryStatusReport } from '../../../../services/ReportService';

import { Table } from '../../../common/table/Table';
import { SaveButton } from '../../../common/button/save-button/SaveButton';

import './SiteQueryStatus.css';

export const SiteQueryStatus = ({
  facility,
  dates,
  resetFilters,
  context,
  setSpinnerText,
  showToaster,
  downloadEnabled,
}) => {
  const [heading, setHeading] = useState('');
  const [results, setResults] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const generateReport = useCallback(async () => {
    if (!facility || !dates) return;

    setSpinnerText('Generating Report');
    const [data, error] = await retrieveSiteQueryStatusReport(context.username, {
      facilityExternalReference: facility.externalReference,
      dates,
    });
    if (data) {
      if (data.responseStatus === 'Success') {
        setHeading(data.heading);
        setResults(data.siteQueryStatusVOS);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  }, [context, setSpinnerText, showToaster, facility, dates]);

  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const handleDownloadExcelClick = async () => {
    setSpinnerText('Downloading Site Query Status Report');
    const [data, headers, error] = await downloadSiteQueryStatusReport(context.username, {
      facilityExternalReference: facility.externalReference,
      dates,
    });
    if (data) {
      const name = extractFileNameFromHeaders(headers);
      downloadFile(data, name);
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const mapDataForGraph = () => {
    const data = [];
    results.forEach((result) => {
      const { studyName, answered, closed, invalidQuery, open, reIssued } = result;

      if (studyName !== 'Grand Total') {
        data.push({
          name: studyName,
          answered: answered === '--' ? 0 : answered,
          closed: closed === '--' ? 0 : closed,
          invalid: invalidQuery === '--' ? 0 : invalidQuery,
          open: open === '--' ? 0 : open,
          reissued: reIssued === '--' ? 0 : reIssued,
        });
      }
    });

    return data;
  };

  const renderLegend = () => {
    const firstResult = results[0];

    return (
      <section className="site-query-status-legend">
        <p className="site-query-status-legend-heading">Legend</p>
        <p className="site-query-status-legend-heading">Grand Totals</p>

        <div className="site-query-status-legend-item">
          <span className="answered" />
          <p>Answered</p>
        </div>

        <div className="site-query-status-legend-item">
          <p>{firstResult?.answered || 0}</p>
        </div>

        <div className="site-query-status-legend-item">
          <span className="closed" />
          <p>Closed</p>
        </div>

        <div className="site-query-status-legend-item">
          <p>{firstResult?.closed || 0}</p>
        </div>

        <div className="site-query-status-legend-item">
          <span className="invalid" />
          <p>Invalid</p>
        </div>

        <div className="site-query-status-legend-item">
          <p>{firstResult?.invalidQuery || 0}</p>
        </div>

        <div className="site-query-status-legend-item">
          <span className="open" />
          <p>Open</p>
        </div>

        <div className="site-query-status-legend-item">
          <p>{firstResult?.open || 0}</p>
        </div>

        <div className="site-query-status-legend-item">
          <span className="reissued" />
          <p>Re-Issued</p>
        </div>

        <div className="site-query-status-legend-item">
          <p>{firstResult?.reIssued || 0}</p>
        </div>

        <div className="site-query-status-legend-item" />

        <div className="site-query-status-legend-item">
          <p>{firstResult?.grandTotal || 0}</p>
        </div>
      </section>
    );
  };

  return (
    <section className="site-query-status-container">
      <div className="site-query-status-content">
        <h1 className="site-query-status-heading">{heading}</h1>
        {showTable ? (
          <Table
            tableData={results}
            tableTitle="Query Status"
            maxRowCount={9}
            hiddenColumns={[]}
            searchEnabled
            filterEnabled
            stickyFirstRow
            horizontalScroll
          />
        ) : (
          <section className="site-query-status-graph">
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={mapDataForGraph()} overflow="visible">
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fill: '#077187', width: 100 }}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="answered" stackId="a" fill="#DC602E" />
                <Bar dataKey="closed" stackId="a" fill="#74A57F" />
                <Bar dataKey="invalid" stackId="a" fill="#6F8695" />
                <Bar dataKey="open" stackId="a" fill="#CD2026" />
                <Bar dataKey="reissued" stackId="a" fill="#E0AC26" />
              </BarChart>
            </ResponsiveContainer>

            {renderLegend()}
          </section>
        )}
      </div>
      <div className="site-query-status-buttons">
        <SaveButton
          title={`View as ${showTable ? 'Graph' : 'Table'}`}
          onClick={() => setShowTable(!showTable)}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        <SaveButton
          title="Reset Filters"
          onClick={resetFilters}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        {downloadEnabled && (
          <SaveButton
            title="Download Excel"
            onClick={handleDownloadExcelClick}
            color="#077187"
            border="1px solid #077187"
          />
        )}
      </div>
    </section>
  );
};
